import { useState, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "@mantine/core";
import { FaExternalLinkAlt, FaArrowLeft, FaCheck } from "react-icons/fa";
import FuelLoading from "../../pages/FuelLoading";
import { useGetProductsQuery } from "../../features/api/ProductApiSlice";
import { toggleCartDrawer } from "../../features/api/cartSlice";
import { useAddItemToCartMutation } from "../../features/api/cartApiSlice";
import { addGuestCartItem } from "../../utils/guestCart";
import Section from "../../components/ui/Section";
import "./productDetailView.scss";

// Printify uses arbitrary option names; existing merchandise uses these legacy fields.
const optionsFor = (variant, isPrintify) => {
  if (!variant) return {};
  if (isPrintify) return variant.optionValues || {};
  return {
    ...(variant.size ? { Size: variant.size } : {}),
    ...(variant.colorName ? { Color: variant.colorName } : {}),
    ...(variant.volume ? { Volume: variant.volume } : {}),
  };
};

const valuesFor = (variant, name, isPrintify) => {
  const value = optionsFor(variant, isPrintify)[name];
  if (value == null || value === "") return [];
  if (!isPrintify && name === "Size") {
    return String(value)
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [String(value)];
};

const purchasable = (variant, isPrintify) =>
  isPrintify ? variant.isAvailable === true : Number(variant.countInStock) > 0;

export default function ProductDetailView() {
  const dispatch = useDispatch();
  const { accessToken, authChecked } = useSelector((state) => state.auth);
  const isAuthenticated = authChecked && Boolean(accessToken);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const requestedVariantId = searchParams.get("variant");
  const [addItemToCart, { isLoading: isAdding }] = useAddItemToCartMutation();
  const { data: products = [], isLoading, isError } = useGetProductsQuery();
  const product = products.find((p) => p._id === id);
  const isPrintify = product?.fulfillmentMethod === "printify";
  const isMerch = product?.productType === "branded_merchandise";

  // Scope manual selections to the current product and optional catalog variant.
  // This prevents a previous product's options from leaking into another product.
  const [selectionOverride, setSelectionOverride] = useState(null);

  const [manuallySelectedImage, setManuallySelectedImage] = useState(null);
  const [addError, setAddError] = useState("");

  const variants = useMemo(() => product?.variants || [], [product]);

  const optionGroups = useMemo(() => {
    const groups = new Map();

    // Printify: display only enabled/available variants.
    // Self-fulfilled: preserve existing merchandise options.
    const selectableVariants = isPrintify
      ? variants.filter((variant) => variant.isAvailable === true)
      : variants;

    for (const variant of selectableVariants) {
      const options = optionsFor(variant, isPrintify);

      for (const optionName of Object.keys(options)) {
        if (!groups.has(optionName)) {
          groups.set(optionName, new Set());
        }

        for (const value of valuesFor(variant, optionName, isPrintify)) {
          groups.get(optionName).add(value);
        }
      }
    }

    return Array.from(groups, ([name, values]) => ({
      name,
      values: Array.from(values),
    }));
  }, [variants, isPrintify]);

  // When the product changes, choose a real, purchasable variant rather than
  // independently choosing options that may not exist together.
  const defaultSelectedOptions = useMemo(() => {
    const initial =
      (isPrintify && requestedVariantId
        ? variants.find(
            (v) => String(v._id) === requestedVariantId && purchasable(v, true)
          )
        : null) ||
      variants.find((v) => purchasable(v, isPrintify)) ||
      variants[0];

    const next = {};

    if (initial) {
      for (const { name } of optionGroups) {
        next[name] = valuesFor(initial, name, isPrintify)[0] || "";
      }
    }

    return next;
  }, [variants, isPrintify, optionGroups, requestedVariantId]);

  const selectionKey = `${id}:${requestedVariantId || ""}`;
  const selectedOptions =
    selectionOverride?.key === selectionKey
      ? selectionOverride.options
      : defaultSelectedOptions;

  const activeSelectedVariant = useMemo(() => {
    if (!variants.length) return null;
    return (
      variants.find((variant) =>
        optionGroups.every(({ name }) =>
          valuesFor(variant, name, isPrintify).includes(selectedOptions[name])
        )
      ) || null
    );
  }, [variants, optionGroups, selectedOptions, isPrintify]);

  const currentImageSet = useMemo(() => {
    const variantImages = activeSelectedVariant?.colorImages;
    return Array.isArray(variantImages) && variantImages.length
      ? variantImages
      : product?.images || [];
  }, [activeSelectedVariant, product]);

  const activeImage =
    manuallySelectedImage && currentImageSet.includes(manuallySelectedImage)
      ? manuallySelectedImage
      : currentImageSet[0] || "";

  const isCurrentSelectionInStock = isPrintify
    ? product?.isPublished === true &&
      activeSelectedVariant?.isAvailable === true
    : product?.inStock === true &&
      (variants.length === 0 ||
        (activeSelectedVariant !== null &&
          Number(activeSelectedVariant.countInStock) > 0));

  const selectedPrice =
    activeSelectedVariant?.price != null
      ? Number(activeSelectedVariant.price)
      : Number(product?.price || 0);

  const handleAttributeChange = (name, value) => {
    const desired = { ...selectedOptions, [name]: value };
    const exact = variants.find(
      (v) =>
        purchasable(v, isPrintify) &&
        optionGroups.every(({ name: groupName }) =>
          valuesFor(v, groupName, isPrintify).includes(desired[groupName])
        )
    );
    if (exact) {
      setSelectionOverride({ key: selectionKey, options: desired });
      setAddError("");
      return;
    }

    // Preserve the option just clicked, and resolve other options to a real variant.
    const alternative = variants.find(
      (v) =>
        purchasable(v, isPrintify) &&
        valuesFor(v, name, isPrintify).includes(value)
    );
    if (!alternative) return;
    const resolved = {};
    for (const group of optionGroups) {
      resolved[group.name] =
        valuesFor(alternative, group.name, isPrintify)[0] || "";
    }
    setSelectionOverride({ key: selectionKey, options: resolved });
    setAddError("");
  };

  const handleAddToCart = async () => {
    if (!authChecked || !product || !isCurrentSelectionInStock || isAdding) {
      return;
    }

    setAddError("");
    if (!product || !isCurrentSelectionInStock || isAdding) return;

    setAddError("");

    try {
      const cartPayload = {
        productId: product._id,
        variantId: activeSelectedVariant?._id || null,

        // Preserve legacy merchandise selections
        size: selectedOptions.Size || "",
        color: selectedOptions.Color || "",
        volume: selectedOptions.Volume || "",

        quantity: 1,
      };

      if (isAuthenticated) {
        // REGISTERED CUSTOMER: Existing MongoDB cart
        await addItemToCart(cartPayload).unwrap();
      } else {
        // GUEST CUSTOMER: Browser localStorage cart
        addGuestCartItem({
          ...cartPayload,

          // Information used to display the guest basket
          name: product.name,
          price: selectedPrice,
          image: activeImage,

          fulfillmentMethod: product.fulfillmentMethod || "self",

          // Preserve all Printify options, including custom option names
          selectedOptions: { ...selectedOptions },

          externalVariantId: activeSelectedVariant?.externalVariantId || "",
        });
      }

      // Open the same cart drawer for both customer types
      dispatch(toggleCartDrawer());
    } catch (error) {
      console.error("Failed to add product to cart:", error);

      setAddError(
        error?.data?.message ||
          error?.message ||
          "Unable to add this item. Please try again."
      );
    }
  };

  if (isLoading) {
    return (
      <div className="product-detail-loading">
        <FuelLoading text="Opening product crates..." />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="product-detail-error">
        <h2>⚠️ Item Inventory Lost</h2>
        <p>
          This product entry doesn't exist or was removed from store shelves.
        </p>
        <Link to="/shop" className="back-shop-link">
          <FaArrowLeft /> Return to Basecamp Shop
        </Link>
      </div>
    );
  }

  return (
    <main className="product-view-page fuel-fade-in">
      <Section>
        <div className="product-view-container">
          <Link to="/shop" className="back-to-catalog-btn">
            <FaArrowLeft /> Back to Shop
          </Link>

          <div className="product-view-grid">
            <div className="product-media-gallery">
              <div className="product-main-hero-frame">
                <img src={activeImage} alt={product.name} />
                {isMerch && !isCurrentSelectionInStock && (
                  <span className="hero-sold-out-badge">Unavailable</span>
                )}
              </div>
              {currentImageSet.length > 0 && (
                <div className="product-thumbs-strip">
                  {currentImageSet.map((imgUrl, idx) => (
                    <button
                      key={`${imgUrl}-${idx}`}
                      type="button"
                      className={`thumb-slot ${
                        activeImage === imgUrl ? "active" : ""
                      }`}
                      onClick={() => setManuallySelectedImage(imgUrl)}
                    >
                      <img
                        src={imgUrl}
                        alt={`${product.name} view ${idx + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="product-meta-details-panel">
              <span className="meta-category-tag">{product.category}</span>
              <h1>{product.name}</h1>
              <div className="detail-price-banner">
                {isMerch ? (
                  <h2>${selectedPrice.toFixed(2)}</h2>
                ) : (
                  <h2 className="affiliate-label-price">
                    {product.priceNote || "Varies by retailer"}
                  </h2>
                )}
              </div>

              <div className="interactive-variants-selection-block">
                {optionGroups.map(({ name, values }) => (
                  <div className="variant-select-group" key={name}>
                    <span className="group-label">Select {name}:</span>
                    <div className="chips-wrapper-row">
                      {values.map((value) => {
                        const isSelected = selectedOptions[name] === value;
                        const available = variants.some(
                          (variant) =>
                            purchasable(variant, isPrintify) &&
                            valuesFor(variant, name, isPrintify).includes(value)
                        );
                        return (
                          <button
                            key={value}
                            type="button"
                            disabled={!available}
                            aria-pressed={isSelected}
                            className={`variant-chip ${
                              name.toLowerCase().includes("color")
                                ? "variant-chip--text"
                                : ""
                            } ${isSelected ? "active" : ""}`}
                            onClick={() => handleAttributeChange(name, value)}
                          >
                            {isSelected &&
                              name.toLowerCase().includes("color") && (
                                <FaCheck className="check-mark-svg" />
                              )}{" "}
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {isMerch && (
                  <div
                    style={{
                      marginTop: "12px",
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                  >
                    {isPrintify && product?.isPublished !== true ? (
                      <span style={{ color: "#f59e0b" }}>
                        🟠 Coming Soon — Not Yet Available for Purchase
                      </span>
                    ) : isCurrentSelectionInStock ? (
                      <span style={{ color: "#10b981" }}>
                        🟢{" "}
                        {isPrintify
                          ? "Available"
                          : activeSelectedVariant &&
                            Number(activeSelectedVariant.countInStock) < 5
                          ? `Running Low! Only ${activeSelectedVariant.countInStock} units left`
                          : "In Stock"}
                      </span>
                    ) : (
                      <span style={{ color: "#ef4444" }}>
                        🔴 This selection is currently unavailable.
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="product-view-action-footer">
                {isMerch ? (
                  <>
                    <button
                      type="button"
                      className="product-view-submit-btn main-purchase-btn"
                      disabled={
                        !authChecked || !isCurrentSelectionInStock || isAdding
                      }
                      onClick={handleAddToCart}
                    >
                      <span className="icon-cart-pad">🛒</span>{" "}
                      {isAdding
                        ? "Adding..."
                        : isPrintify && product?.isPublished !== true
                        ? "Coming Soon"
                        : isCurrentSelectionInStock
                        ? "Add Product to Cart"
                        : "Out of Stock"}
                    </button>
                    {addError && (
                      <p role="alert" style={{ color: "#ef4444" }}>
                        {addError}
                      </p>
                    )}
                  </>
                ) : (
                  <a
                    href={
                      product.affiliateLinks?.[0]?.url || "https://amazon.com"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="product-view-submit-btn main-affiliate-btn"
                  >
                    Purchase on Retailer Network <FaExternalLinkAlt />
                  </a>
                )}
              </div>

              <Divider my="xl" style={{ opacity: 0.05 }} />
              <div className="product-view-rich-narrative">
                <h3>Product Specifications &amp; Notes</h3>
                <div
                  className="rich-parsed-html-content"
                  dangerouslySetInnerHTML={{
                    __html: product.description || "",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}
